import type { FC } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@krea/shared/design-system/Button";

import type { IFIBankIDProps } from "../common-types";
import { BankIDAuthenticationState } from "../components/BankIDAuthenticationState";
import { BankSelectionFlow } from "./components/BankSelectionFlow";
import { OAuthPendingFlow } from "./components/OAuthPendingFlow";
import { useFIBankID } from "./useFIBankID";

export const FIBankIDFlow: FC<IFIBankIDProps> = ({
  authenticationContext,
  identificationContext,
  onSuccess,
  onError,
  onCancel,
  extraData,
}) => {
  const { t } = useTranslation();

  const bankid = useFIBankID({
    authenticationContext,
    identificationContext,
    onSuccess,
    onError,
    onCancel,
    extraData,
  });

  const handleBankSelect = (id: string) => {
    bankid.selectBank(id);
  };

  const handleCancel = () => {
    bankid.cancel();
  };

  // Render: Success state
  if (bankid.state.status === "success") {
    return (
      <div className="flex w-full flex-col items-center">
        <h2 className="mb-6 text-center">
          {t("bankID.common.pleaseWait")}
        </h2>
      </div>
    );
  }

  // Render: Error state
  if (bankid.state.status === "error") {
    const isUserCancellation = bankid.state.error.isUserCancellation;

    return (
      <div className="flex w-full flex-col items-center">
        {isUserCancellation && (
          <h2 className="mb-6 text-center">
            {t("bankID.common.canceledTitle")}
          </h2>
        )}
        <BankIDAuthenticationState error={!isUserCancellation}>
          {isUserCancellation
            ? t("bankID.common.loginCancelled")
            : bankid.state.error.message || t("bankID.errors.genericError")}
        </BankIDAuthenticationState>
        <Button
          variant="ghost"
          className="mt-6 w-full"
          onClick={handleCancel}
        >
          {t("bankID.common.back")}
        </Button>
      </div>
    );
  }

  // Render: Pending (OAuth in progress) state
  if (bankid.state.status === "pending") {
    return (
      <div className="flex w-full flex-col items-center">
        <h2 className="mb-6 text-center">
          {t("bankID.FI.authInProgress")}
        </h2>
        <OAuthPendingFlow />
        <Button
          variant="ghost"
          className="mt-6 w-full"
          onClick={handleCancel}
        >
          {t("bankID.common.cancel")}
        </Button>
      </div>
    );
  }

  // Render: Initial/Authenticating state - show bank selection
  return (
    <div className="flex w-full flex-col items-center">
      <BankSelectionFlow
        banks={bankid.identityProviders}
        onSelect={handleBankSelect}
        isLoading={bankid.state.status === "idle"}
      />

      {onCancel && (
        <Button
          variant="ghost"
          className="mt-6 w-full"
          onClick={handleCancel}
        >
          {t("bankID.common.back")}
        </Button>
      )}
    </div>
  );
};
